import {useEffect} from 'react';

type refType = HTMLDivElement | HTMLSpanElement;
interface UseParagraphEditorProps {
    content: string;
    contentEditableRef: React.RefObject<refType>;
    onChange?: (content: string) => void;
    onFocus?: () => void;
    onEditableFocus?: (element: HTMLElement | null) => void;
    onEditableCursorMove?: () => void;
}
export const EMPTY_PARAGRAPH = '--';
export function useParagraphEditor({
    content,
    contentEditableRef,
    onChange,
    onFocus,
    onEditableFocus,
    onEditableCursorMove
}: UseParagraphEditorProps) {
    useEffect(() => {
        if(contentEditableRef.current) {
            const currentHtml = contentEditableRef.current.innerHTML || '';
            if(currentHtml !== content) {
                if(document.activeElement !== contentEditableRef.current) {
                    contentEditableRef.current.innerHTML = content;
                }
            }
        }
    }, [content, contentEditableRef]);

    const handleInput = (e: React.FormEvent<refType>) => { 
        const newHtmlContent = e.currentTarget.innerHTML || '';
        onChange?.(newHtmlContent);
    };

    const handleKeyDown = (e: React.KeyboardEvent<refType>) => {
        if(e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<refType>) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        const singleLineText = text.replace(/\n/g, ' ');
        const selection = window.getSelection();
        if(!selection || !selection.rangeCount) return;
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(singleLineText));
        range.setStartAfter(range.endContainer);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    };

    const handleBlur = (e: React.FocusEvent<refType>) => {
        const currentContent = e.currentTarget.innerHTML || '';
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = currentContent;
        const textContent = tempDiv.textContent || '';
        const trimmedText = textContent.trim();
        if(trimmedText === '' && contentEditableRef.current) {
            onChange?.(EMPTY_PARAGRAPH);
        }
    };

    const handleFocus = () => {
        onFocus?.();
        onEditableFocus?.(contentEditableRef.current ?? null);
    };

    const handleCursorMove = () => {
        onEditableCursorMove?.();
    };

    return {
        handleInput,
        handleKeyDown,
        handlePaste,
        handleBlur,
        handleFocus,
        handleCursorMove
    };
}
